import React from 'react';
import { ErrorState } from '../design-system/components/ErrorState';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
  at?: Date;
}

/**
 * 전역 에러 경계 — 개별 컴포넌트 렌더 에러가 전체 화이트스크린으로
 * 번지는 것을 방지한다 (프로덕션 안전망, 2026-07-07).
 *
 * 에러 시 v2 S3 화면(다시 시도·전화·오류코드)을 보여주고, 콘솔에는 개발 환경에서만 기록.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true, at: new Date() };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('[ErrorBoundary]', error, info.componentStack);
    }
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  private handleGoHome = (): void => {
    window.location.href = '/';
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      // v2 S3 통신/앱 오류 화면 — 다시 시도(새로고침) + 전화(대표번호) + 오류코드·시각 (2026-09-21)
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center">
          <ErrorState
            title="일시적인 오류가 발생했어요"
            onRetry={this.handleReload}
            code="E-APP"
            at={this.state.at}
          />
          <button
            type="button"
            onClick={this.handleGoHome}
            className="-mt-8 text-[13px] font-bold text-muted underline-offset-2 hover:underline"
          >
            홈으로
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
